import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { DateString } from '../api/services/orders/types.ts'
import { PricesList } from '../api/services/pricing/types.ts'
import { userAssignmentCodes } from '../api/services/user_assignment_codes'
import {
  UserCode,
  UserCodeFormikData
} from '../api/services/user_assignment_codes/types.ts'
import { convertToShortDate } from '../utils/formatter.ts'

export const useAddCodeFormik = (
  editObject: UserCode | null,
  setChangeStatus: React.Dispatch<React.SetStateAction<boolean>>,
  setIsPopupVisible: React.Dispatch<React.SetStateAction<boolean>>,
  collapsePricing: boolean,
  userPrices: PricesList | undefined
) => {
  const currentDate = convertToShortDate(new Date().toString()) as DateString
  const valid_until = editObject?.valid_until || null

  const initialValues: UserCodeFormikData = {
    code: editObject ? editObject.code : '',
    description: editObject ? editObject.description : '',
    valid_from: editObject !== null ? editObject.valid_from : currentDate,
    valid_until: valid_until,

    month_diet_price: editObject
      ? editObject.special_pricing?.month_diet_price
      : 0,
    three_month_diet_price: editObject
      ? editObject.special_pricing?.three_month_diet_price
      : 0,
    six_month_diet_price: editObject
      ? editObject.special_pricing?.six_month_diet_price
      : 0,
    twelve_month_diet_price: editObject
      ? editObject.special_pricing?.twelve_month_diet_price
      : 0
  }

  const hasChanges = (next: UserCodeFormikData) => {
    const sameBase =
      next.code === initialValues.code &&
      next.description === initialValues.description &&
      next.valid_from === initialValues.valid_from &&
      next.valid_until === initialValues.valid_until

    if (!sameBase) return true

    if (collapsePricing) {
      next.month_diet_price = undefined
      next.three_month_diet_price = undefined
      next.six_month_diet_price = undefined
      next.twelve_month_diet_price = undefined
    }

    return (
      next.month_diet_price !== initialValues.month_diet_price ||
      next.three_month_diet_price !== initialValues.three_month_diet_price ||
      next.six_month_diet_price !== initialValues.six_month_diet_price ||
      next.twelve_month_diet_price !== initialValues.twelve_month_diet_price
    )
  }

  const validationSchema = Yup.object({
    code: Yup.string()
      .required('To pole jest wymagane')
      .min(6, 'Kod musi składać się z co najmniej 6 znaków')
      .max(12, 'Kod może mieć maksymalnie 12 znaków'),
    description: Yup.string()
      .nullable()
      .notRequired()
      .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue.trim() === ''
          ? null
          : value
      ),
    valid_from: Yup.string()
      .required('Data jest wymagane')
      .test(
        'is-valid-range', // Name of the test
        'Data początkowa nie może być większa lub równa niż data końcowa', // Validation error message
        function (value) {
          const { valid_until } = this.parent // Access other fields using `this.parent`
          if (!value || !valid_until) {
            // If either date is not provided, skip this validation
            return true
          }
          return new Date(value) < new Date(valid_until)
        }
      ),
    ...(!collapsePricing && {
      month_diet_price: Yup.number()
        .required('Przy ustawianiu cennika to pole nie może być puste.')
        .test(
          'max-month_diet_price',
          `Kwota nie może być większa od domyślnej ${userPrices?.month_diet_price} PLN`, // Error message
          (value) => {
            const limit = userPrices?.month_diet_price ?? 0
            const limitNumber = Number(limit)
            return value !== undefined && value <= limitNumber
          }
        )
        .test(
          'max-month_diet_price',
          'Minimalna wartość dla płatności wynosi 2 PLN', // Error message
          (value) => {
            return value > 1.99
          }
        ),
      three_month_diet_price: Yup.number()
        .required('Przy ustawianiu cennika to pole nie może być puste.')
        .test(
          'max-three_month_diet_price',
          `Kwota nie może być większa od domyślnej ${userPrices?.three_month_diet_price} PLN`, // Error message
          (value) => {
            const limit = userPrices?.three_month_diet_price ?? 0
            const limitNumber = Number(limit)
            return value !== undefined && value <= limitNumber
          }
        )
        .test(
          'max-three_month_diet_price',
          'Minimalna wartość dla płatności wynosi 2 PLN', // Error message
          (value) => {
            return value > 1.99
          }
        ),
      six_month_diet_price: Yup.number()
        .required('Przy ustawianiu cennika to pole nie może być puste.')
        .test(
          'max-six_month_diet_price',
          `Kwota nie może być większa od domyślnej ${userPrices?.six_month_diet_price} PLN`, // Error message
          (value) => {
            const limit = userPrices?.six_month_diet_price ?? 0
            const limitNumber = Number(limit)
            return value !== undefined && value <= limitNumber
          }
        )
        .test(
          'max-six_month_diet_price',
          'Minimalna wartość dla płatności wynosi 2 PLN', // Error message
          (value) => {
            return value > 1.99
          }
        ),
      twelve_month_diet_price: Yup.number()
        .required('Przy ustawianiu cennika to pole nie może być puste.')
        .test(
          'max-six_month_diet_price',
          `Kwota nie może być większa od domyślnej ${userPrices?.twelve_month_diet_price} PLN`, // Error message
          (value) => {
            const limit = userPrices?.twelve_month_diet_price ?? 0
            const limitNumber = Number(limit)
            return value !== undefined && value <= limitNumber
          }
        )
        .test(
          'max-six_month_diet_price',
          'Minimalna wartość dla płatności wynosi 2 PLN', // Error message
          (value) => {
            return value > 1.99
          }
        )
    })
  })

  const createUserCode = async (
    values: UserCodeFormikData,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      await userAssignmentCodes.createCode({
        code: values.code,
        description: values.description,
        valid_from: values.valid_from,
        valid_until: values.valid_until,
        special_pricing: collapsePricing
          ? null
          : {
              month_diet_price: values.month_diet_price,
              three_month_diet_price: values.three_month_diet_price,
              six_month_diet_price: values.six_month_diet_price,
              twelve_month_diet_price: values.twelve_month_diet_price
            }
      })
    } catch (error) {
      console.log(error)
    } finally {
      setSubmitting(false)
    }
  }

  const updateUserCode = async (
    values: UserCodeFormikData,
    setSubmitting: (isSubmitting: boolean) => void,
    resetForm: (nextState?: { values: UserCodeFormikData }) => void
  ) => {
    if (!editObject) return

    if (!hasChanges(values)) {
      toast.info('Brak zmian do zapisania')
      setSubmitting(false)
      return
    }

    const toastId = toast.loading('Proszę czekać, trwa wysyłanie żądania')

    try {
      await userAssignmentCodes.updateCode(editObject.id, {
        code: values.code,
        description: values.description,
        valid_from: values.valid_from,
        valid_until: values.valid_until,
        special_pricing: collapsePricing
          ? null
          : {
              month_diet_price: values.month_diet_price,
              three_month_diet_price: values.three_month_diet_price,
              six_month_diet_price: values.six_month_diet_price,
              twelve_month_diet_price: values.twelve_month_diet_price
            }
      })
      resetForm({ values })
      toast.update(toastId, {
        render: 'Zmiany zostały zapisane',
        type: 'success',
        delay: 1000,
        isLoading: false,
        autoClose: 3000
      })
    } catch (error) {
      toast.dismiss(toastId)
      console.log(error)
    } finally {
      setSubmitting(false)
    }
  }

  return useFormik<UserCodeFormikData>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (editObject) await updateUserCode(values, setSubmitting, resetForm)
      else await createUserCode(values, setSubmitting)
      setChangeStatus(true)
      setIsPopupVisible(false)
    }
  })
}
